<template>
    <div aria-live="assertive" class="upcoming-tests-new-page">
        <div class="col-xs-12">
            <div class="d-flex-row flex-justify-between">
                <p id="title" role="heading" aria-level="1" tabindex="0">
                    <i class="fa fa-calendar"></i> Provas futuras
                </p>
            </div>
        </div>

        <div v-if="!loading">
            <div v-if="result && result.length > 0">
                <div class="col-xs-12 p-x-sm d-flex-row flex-wrap">
                    <div class="col-xs-12 p-md m-b-md">
                        <div class="card p-20 filter-card">
                            <div class="search-input-wrapper m-b-xl">
                                <i class="fa fa-search"></i>
                                <input
                                    type="text"
                                    v-model="searchQuery"
                                    placeholder="Buscar por disciplina ou avaliação..."
                                    class="search-input"
                                />
                            </div>

                            <div class="finished-filter-row">
                                <FilterMultiDropdown
                                    label="Módulo"
                                    icon="fa fa-filter"
                                    :options="moduleOptions"
                                    v-model="selectedModules"
                                />
                                <FilterMultiDropdown
                                    label="Tipo"
                                    icon="fa fa-tag"
                                    :options="typeOptions"
                                    v-model="selectedTypes"
                                />
                            </div>

                            <div class="d-flex-row flex-justify-end m-t-md" v-if="hasActiveFilters">
                                <button class="btn btn-default" @click="clearFilters">
                                    Limpar filtros
                                </button>
                            </div>
                        </div>
                    </div>

                    <div
                        v-for="test in sortedTests"
                        :key="test"
                        class="col-xs-12 col-md-6 col-lg-3 p-md m-b-md"
                    >
                        <BaseTestCard :test="test" :module-name="test.module">
                            <template #header-right />
                            <template #footer>
                                <div class="col-xs-12 p-0 m-b-md d-flex-row flex-justify-between flex-align-center" v-if="!test.configs.param_hide_online_test_date">
                                    <small class="info">
                                        <i class="fa fa-calendar"></i> {{ formatDbDate(test.schedule.date_schedule) }} às {{ test.schedule.time_schedule }}
                                    </small>
                                </div>
                                <div class="col-xs-12 p-0 m-b-md d-flex-row flex-justify-between flex-align-center">
                                    <small class="info">
                                        <i class="fa fa-map-marker fa-lg"></i> {{ test.place }}
                                    </small>
                                </div>
                                <div v-if="isPendingRelease(test)" class="upcoming-pending-warning m-t-md">
                                    <i class="fa fa-exclamation-triangle"></i> Prova pendente de liberação.
                                </div>
                            </template>
                        </BaseTestCard>
                    </div>

                    <div v-if="filteredTests.length === 0" class="col-xs-12 d-flex-row flex-align-center flex-justify-center p-xl">
                        <img src="../../assets/img/search-logo.png" alt="Nenhum resultado" class="m-r-xl" />
                        <div class="d-flex-column">
                            <div class="no-results-title">Nenhum resultado encontrado</div>
                            <div>Não há registros que atendam aos filtros aplicados</div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="col-xs-12 d-flex-row flex-align-center flex-justify-center p-xl">
                <img src="../../assets/img/no-results-logo.png" alt="Nenhum resultado" class="m-r-xl" />
                <div class="d-flex-column">
                    <div class="no-results-title">Nenhum resultado encontrado</div>
                    <div>Não há provas futuras disponíveis.</div>
                </div>
            </div>
        </div>

        <div v-else role="presentation" class="col-xs-12 d-flex-column align-center flex-justify-center text-center h-100vh overflow-hidden">
            <i class="fa fa-5x fa-circle-o-notch fa-spin"></i>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { getUpcomingTests } from '@/scripts/apiService/tests';
import { extractModule, formatDbDate } from '@/scripts/utils';
import FilterMultiDropdown from '../base/FilterMultiDropdown.vue';
import BaseTestCard from '../base/BaseTestCard.vue';

const result = ref(null);
const loading = ref(false);
const searchQuery = ref('');
const activeSearchQuery = ref('');
const selectedModules = ref([]);
const selectedTypes = ref([]);

function isPendingRelease(test) {
    const now = new Date();
    const inputDate = new Date(`${test.schedule.date_schedule}T${test.schedule.time_schedule}`);
    return now > inputDate;
}

const moduleOptions = computed(() => {
    if (!result.value) return [];
    const modules = [...new Set(result.value.map(t => extractModule(t.academic)).filter(Boolean))].sort();
    return modules.map(m => ({ value: m, label: m }));
});

const typeOptions = computed(() => {
    if (!result.value) return [];
    const types = [...new Set(result.value.map(t => t.schedule.type_assessment.name))].sort();
    return types.map(t => ({ value: t, label: t }));
});

watch(searchQuery, (val) => {
    if (val.length === 0 || val.length >= 3) {
        activeSearchQuery.value = val;
    }
});

const hasActiveFilters = computed(() =>
    activeSearchQuery.value || selectedModules.value.length || selectedTypes.value.length
);

const testsWithData = computed(() => {
    if (!result.value) return [];
    return result.value.map(test => ({
        ...test,
        module: extractModule(test.academic)
    }));
});

const filteredTests = computed(() => {
    let tests = testsWithData.value;

    if (activeSearchQuery.value) {
        const normalize = str => str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const query = normalize(activeSearchQuery.value);
        tests = tests.filter(t =>
            normalize(t.academic).includes(query) ||
            normalize(t.schedule.type_assessment.name).includes(query)
        );
    }

    if (selectedModules.value.length) {
        tests = tests.filter(t => selectedModules.value.includes(t.module));
    }

    if (selectedTypes.value.length) {
        tests = tests.filter(t => selectedTypes.value.includes(t.schedule.type_assessment.name));
    }

    return tests;
});

const sortedTests = computed(() =>
    [...filteredTests.value].sort((a, b) => {
        const moduleCompare = (a.module || '').localeCompare(b.module || '');
        if (moduleCompare !== 0) return moduleCompare;

        const dateCompare = (a.schedule?.date_schedule || '').localeCompare(b.schedule?.date_schedule || '');
        if (dateCompare !== 0) return dateCompare;

        const timeCompare = (a.schedule?.time_schedule || '').localeCompare(b.schedule?.time_schedule || '');
        if (timeCompare !== 0) return timeCompare;

        return (a.schedule?.type_assessment?.name || '').localeCompare(b.schedule?.type_assessment?.name || '');
    })
);

function clearFilters() {
    searchQuery.value = '';
    activeSearchQuery.value = '';
    selectedModules.value = [];
    selectedTypes.value = [];
}

onMounted(() => {
    loading.value = true;
    getUpcomingTests()
        .then((response) => {
            result.value = response.data;
        })
        .catch(() => {})
        .finally(() => {
            loading.value = false;
        });
    document.getElementById('title')?.focus();
});
</script>
